import type { AxiosError } from 'axios';
import Axios from 'axios';
import { HTTPStatusCodes } from 'app/utils/httpStatuses';
import { csrfProtection } from 'app/utils/csrfProtection';

interface TokenErrorResponse {
  detail: string;
  code: string;
  messages: Array<{
    token_class: string;
    token_type: string;
    message: string;
  }>;
}

interface TokenErrorResponse {
  detail: string;
  code: string;
  messages: Array<{
    token_class: string;
    token_type: string;
    message: string;
  }>;
}

function isAxiosError(e: Error | AxiosError): e is AxiosError {
  return 'isAxiosError' in e && e.isAxiosError;
}

function isAuthError(e: AxiosError) {
  // Check HTTP status code
  if (e.response?.status === 401 || e.response?.status === 403) {
    return true;
  }

  // Check specific Django REST error format
  const errorData = e.response?.data as { code?: string };
  if (errorData?.code === 'token_not_valid') {
    return true;
  }
  if (e.code === HTTPStatusCodes.ClientError.Unauthorized) {
    return true;
  }
  if (
    e.response &&
    e.response.status.toString() === HTTPStatusCodes.ClientError.Unauthorized
  ) {
    return true;
  }
  return false;
}

function addRequestInterceptor(config: { token: { current: string } }) {
  const { token } = config;
  return Axios.interceptors.request.use(
    conf => {
      if (token.current) {
        conf.headers!.Authorization = `Bearer ${token.current}`;
      }
      
      // Add CSRF protection for non-safe methods
      if (conf.method && !['get', 'head', 'options'].includes(conf.method.toLowerCase())) {
        const csrfHeaders = csrfProtection.getHeaders();
        Object.assign(conf.headers!, csrfHeaders);
      }
      
      return conf;
    },
    e => e,
  );
}

function addResponseInterceptor(config: {
  refreshToken: { current: string | undefined };
  onRequestError: () => void;
  tokenRefreshMethod: (rt: string) => Promise<boolean>;
}) {
  const { onRequestError, refreshToken, tokenRefreshMethod } = config;
  const refreshQueue: Set<string> = new Set();

  return Axios.interceptors.response.use(
    success => {
      refreshQueue.delete(success.request.responseURL);
      return success;
    },
    async (error:any) => {
      if (isAuthError(error)) {
        if (refreshQueue.has(error.request.responseURL)) {
          // If we've already tried to refresh for this URL
          localStorage.setItem('sessionExpired', 'true'); // Add this line
          refreshQueue.delete(error.request.responseURL);
          onRequestError();
        } else {
          refreshQueue.add(error.request.responseURL);
          if (refreshToken.current) {
            try {
              const success = await tokenRefreshMethod(refreshToken.current);
              if (success) {
                return Axios.request(error.config);
              }
            } catch (refreshError) {
              localStorage.setItem('sessionExpired', 'true'); // Add this line
              refreshQueue.delete(error.request.responseURL);
              onRequestError();
              return Promise.reject(error);
            }
          }
          // If we don't have a refresh token or refresh failed
          localStorage.setItem('sessionExpired', 'true'); // Add this line
          refreshQueue.delete(error.request.responseURL);
          onRequestError();
        }
      }
      return Promise.reject(error);
    },
  );
}

export function addAxiosInterceptors(options: {
  token: { current: string };
  refreshToken: { current: string | undefined };
  tokenRefreshMethod: (rt: string) => Promise<boolean>;
  onRequestError: () => void;
}): [requestInterceptorId: number, responseInterceptorId: number] {
  const { token, onRequestError, refreshToken, tokenRefreshMethod } = options;

  const requestInterceptor = addRequestInterceptor({ token });
  const responseInterceptor = addResponseInterceptor({
    onRequestError,
    refreshToken,
    tokenRefreshMethod,
  });

  return [requestInterceptor, responseInterceptor];
}
